// Tile cache (DRAW_PLAN §8): the view is cut into 512×512 screen tiles aligned to the world at the current
// zoom. A tile is drawn once and reused while panning; an edit invalidates only the tiles under the changed
// world rectangles; while the zoom changes, the old tiles are drawn scaled until the zoom has been still
// for 150 ms, then they are redrawn sharp.
import type { Doc, Rect } from '../model/types';
import type { View } from '../state';
import { DisplayList } from './displayList';
import { paintItem, cssColor } from './paint';
import { cssVar } from '../../app/theme';

export const TILE = 512;
const MAX_TILES = 64;
/** The pasteboard around artboards: the neutral photo surround (M18), read from global.css. */
export const pasteboard = () => cssVar('--photo-surround');
const SHADOW_PAD = 12; // artboard shadow reach in screen px

/** A finished tile is an ImageBitmap (a GPU texture: drawing it into the view is a cheap blit). */
type Tile = { i: number; j: number; bitmap: ImageBitmap | null; valid: boolean; used: number };

export class TileRenderer {
  readonly list = new DisplayList();
  private tiles = new Map<string, Tile>();
  private zoom = 0;
  private dpr = 1;
  private zoomChangedAt = 0;
  private stale: { tiles: Map<string, Tile>; zoom: number } | null = null;
  private frame = 0;
  /** One drawing surface shared by all tiles; each finished tile is transferred out as a bitmap. */
  private scratch: OffscreenCanvas | null = null;
  /** Stats for the benchmarks. */
  stats = { tilesDrawn: 0, itemsDrawn: 0 };

  /** Top ids drawn on top of the tiles every frame instead of into them (what is being dragged). */
  private floating = new Set<string>();

  /** Call when the document changes. */
  setDoc(doc: Doc) {
    const prev = this.lastDoc;
    this.lastDoc = doc;
    const dirty = this.list.update(doc);
    // Global swatches (brand colours) are resolved at paint time: a swatch edit repaints everything.
    if (prev && prev.swatches !== doc.swatches) {
      this.invalidateAll();
      return;
    }
    if (dirty === null) for (const t of this.tiles.values()) t.valid = false;
    else for (const it of this.list.changed) if (!this.floating.has(it.topId)) this.invalidate(it.bounds);
  }

  /** Starts / ends floating (a drag): the tiles under those objects are redrawn without / with them once. */
  setFloating(ids: Iterable<string>) {
    const next = new Set(ids);
    const touched = new Set(
      [...this.floating, ...next].filter((id) => this.floating.has(id) !== next.has(id)),
    );
    if (!touched.size) return;
    for (const it of this.list.items) if (touched.has(it.topId)) this.invalidate(it.bounds);
    this.floating = next;
  }

  /** Everything redraws (fonts loaded, an image decoded, brand colours changed). */
  invalidateAll() {
    for (const t of this.tiles.values()) t.valid = false;
  }

  /** Fonts finished loading: text layout changed for every object. */
  rebuild() {
    this.list.reset();
    const doc = this.lastDoc;
    if (doc) this.list.update(doc);
    this.invalidateAll();
  }
  private lastDoc: Doc | null = null;

  invalidate(r: Rect) {
    const s = TILE / this.zoom;
    if (!this.zoom) return;
    const i0 = Math.floor(r.x / s),
      i1 = Math.floor((r.x + r.w) / s),
      j0 = Math.floor(r.y / s),
      j1 = Math.floor((r.y + r.h) / s);
    for (let i = i0 - 1; i <= i1 + 1; i++)
      for (let j = j0 - 1; j <= j1 + 1; j++) {
        const t = this.tiles.get(`${i},${j}`);
        if (t) t.valid = false;
      }
  }

  /** Draws the view onto `g` (device pixels). Returns true when another frame is needed (zoom settling). */
  render(g: CanvasRenderingContext2D, v: View, dpr: number, now = performance.now()): boolean {
    this.frame++;
    this.stats = { tilesDrawn: 0, itemsDrawn: 0 };
    if (v.zoom !== this.zoom || dpr !== this.dpr) {
      if (this.tiles.size && dpr === this.dpr) this.stale = { tiles: this.tiles, zoom: this.zoom };
      this.tiles = new Map();
      this.zoom = v.zoom;
      this.dpr = dpr;
      this.zoomChangedAt = now;
    }
    const W = g.canvas.width,
      H = g.canvas.height;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = pasteboard();
    g.fillRect(0, 0, W, H);
    const settling = this.stale && now - this.zoomChangedAt < 150;
    if (settling && this.stale) {
      // Draw the previous zoom's tiles, scaled: cheap and good enough while the wheel is turning.
      const k = v.zoom / this.stale.zoom;
      for (const t of this.stale.tiles.values()) {
        const sx = (t.i * TILE * k + v.panX) * dpr,
          sy = (t.j * TILE * k + v.panY) * dpr;
        if (t.bitmap) g.drawImage(t.bitmap, sx, sy, TILE * k * dpr, TILE * k * dpr);
      }
      return true;
    }
    this.stale = null;
    const i0 = Math.floor(-v.panX / TILE),
      j0 = Math.floor(-v.panY / TILE),
      i1 = Math.floor((W / dpr - v.panX) / TILE),
      j1 = Math.floor((H / dpr - v.panY) / TILE);
    for (let i = i0; i <= i1; i++)
      for (let j = j0; j <= j1; j++) {
        const t = this.tile(i, j);
        if (t.bitmap)
          g.drawImage(t.bitmap, Math.round((i * TILE + v.panX) * dpr), Math.round((j * TILE + v.panY) * dpr));
      }
    // Floating objects: drawn straight onto the view every frame.
    if (this.floating.size) {
      g.setTransform(dpr * v.zoom, 0, 0, dpr * v.zoom, dpr * v.panX, dpr * v.panY);
      for (const it of this.list.items)
        if (this.floating.has(it.topId) && !it.hidden) paintItem(g, it, this.list.doc);
    }
    this.evict();
    return false;
  }

  private tile(i: number, j: number): Tile {
    const key = `${i},${j}`;
    let t = this.tiles.get(key);
    if (!t) {
      t = { i, j, bitmap: null, valid: false, used: 0 };
      this.tiles.set(key, t);
    }
    t.used = this.frame;
    if (!t.valid) this.draw(t);
    return t;
  }

  private draw(t: Tile) {
    const doc = this.list.doc;
    if (!this.scratch || this.scratch.width !== TILE * this.dpr)
      this.scratch = new OffscreenCanvas(TILE * this.dpr, TILE * this.dpr);
    const canvas = this.scratch;
    const g = canvas.getContext('2d')!;
    const d = this.dpr,
      z = this.zoom;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalAlpha = 1;
    g.globalCompositeOperation = 'source-over';
    g.fillStyle = pasteboard();
    g.fillRect(0, 0, canvas.width, canvas.height);
    // world → tile pixels: (world · z − tile origin) · dpr
    g.setTransform(d * z, 0, 0, d * z, -t.i * TILE * d, -t.j * TILE * d);
    const wx = (t.i * TILE) / z,
      wy = (t.j * TILE) / z,
      ws = TILE / z;
    if (doc)
      for (const a of doc.artboards) {
        if (a.x > wx + ws + SHADOW_PAD / z || a.x + a.w < wx - SHADOW_PAD / z) continue;
        if (a.y > wy + ws + SHADOW_PAD / z || a.y + a.h < wy - SHADOW_PAD / z) continue;
        g.save();
        g.shadowColor = cssVar('--artboard-shadow');
        g.shadowBlur = 8 * d;
        g.shadowOffsetY = 2 * d;
        g.fillStyle = a.background?.kind === 'solid' ? cssColor(a.background.color, doc) : cssVar('--pure-white');
        g.fillRect(a.x, a.y, a.w, a.h);
        g.restore();
      }
    for (const it of this.list.query({ x: wx - 1 / z, y: wy - 1 / z, w: ws + 2 / z, h: ws + 2 / z })) {
      if (it.hidden || this.floating.has(it.topId)) continue;
      paintItem(g as unknown as CanvasRenderingContext2D, it, doc);
      this.stats.itemsDrawn++;
    }
    t.bitmap?.close();
    t.bitmap = canvas.transferToImageBitmap();
    t.valid = true;
    this.stats.tilesDrawn++;
  }

  private evict() {
    if (this.tiles.size <= MAX_TILES) return;
    const sorted = [...this.tiles.entries()].sort((a, b) => a[1].used - b[1].used);
    for (const [k, t] of sorted.slice(0, this.tiles.size - MAX_TILES)) {
      t.bitmap?.close();
      this.tiles.delete(k);
    }
  }
}
